import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, CheckCircle, Mail, Lock, Loader2, ArrowRight, ArrowLeft, Briefcase } from 'lucide-react';
import { LanguageSwitcher } from '../LanguageSwitcher';

interface LoginViewProps {
    onLogin: (email: string, password: string) => Promise<void>;
    onGoogleLogin: () => Promise<void>;
    onRegister: (email: string, password: string, language?: string, companyName?: string) => Promise<void>;
    onResetPassword: (email: string) => Promise<void>;
}

export const LoginView: React.FC<LoginViewProps> = ({ onLogin, onGoogleLogin, onRegister, onResetPassword }) => {
    const { t, i18n } = useTranslation('auth');
    const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
    const [email, setEmail] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMsg(null);

        try {
            if (mode === 'login') {
                await onLogin(email, password);
            } else if (mode === 'register') {
                const lang = i18n.language || 'en';
                await onRegister(email, password, lang, companyName);
            } else if (mode === 'forgot') {
                await onResetPassword(email);
                setSuccessMsg(t('messages.reset_sent'));
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-blue-100 rounded-full blur-3xl opacity-50"></div>
                <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] bg-purple-100 rounded-full blur-3xl opacity-50"></div>
            </div>

            <div className="w-full max-w-md bg-white rounded-[2rem] shadow-xl border border-slate-100 p-8 relative z-10 animate-fadeIn">

                {/* Language Switcher */}
                <div className="flex justify-end mb-4">
                    <div className="w-32 bg-slate-50 rounded-xl">
                        <LanguageSwitcher collapsed={false} />
                    </div>
                </div>

                {/* Logo Section */}
                <div className="flex flex-col items-center mb-8">
                    <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center text-white mb-4 shadow-lg shadow-slate-200">
                        <span className="font-bold text-3xl"><img src="/logo-b.svg" width="50" alt="Accounting Flow" /></span>
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 text-center">
                        {mode === 'login' && t('titles.login')}
                        {mode === 'register' && t('titles.register')}
                        {mode === 'forgot' && t('titles.forgot')}
                    </h1>
                    <p className="text-slate-500 text-center text-sm mt-2 max-w-xs">
                        {mode === 'login' && t('subtitles.login')}
                        {mode === 'register' && t('subtitles.register')}
                        {mode === 'forgot' && t('subtitles.forgot')}
                    </p>
                </div>

                {/* Error / Success Messages */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-100 text-red-600 p-3 rounded-xl text-sm flex items-start gap-2">
                        <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {successMsg && (
                    <div className="mb-6 bg-green-50 border border-green-100 text-green-600 p-3 rounded-xl text-sm flex items-start gap-2">
                        <CheckCircle size={16} className="mt-0.5 shrink-0" />
                        <span>{successMsg}</span>
                    </div>
                )}

                {/* Google Sign In */}
                {(mode === 'login' || mode === 'register') && (
                    <div className="mb-6">
                        <button
                            type="button"
                            onClick={async () => {
                                setLoading(true);
                                setError(null);
                                try {
                                    await onGoogleLogin();
                                } catch (err: any) {
                                    setError(err.message);
                                    setLoading(false);
                                }
                            }}
                            disabled={loading}
                            className="w-full bg-white text-slate-700 font-bold py-3 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-3 relative overflow-hidden group"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    fill="#4285F4"
                                />
                                <path
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    fill="#34A853"
                                />
                                <path
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z"
                                    fill="#FBBC05"
                                />
                                <path
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    fill="#EA4335"
                                />
                            </svg>
                            <span className="text-sm">{t('buttons.google')}</span>
                        </button>

                        <div className="relative mt-6 flex items-center justify-center">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-slate-200"></div>
                            </div>
                            <span className="relative bg-white px-4 text-xs font-bold text-slate-400 uppercase tracking-widest">{t('texts.or')}</span>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === 'register' && (
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1 ml-1">{t('labels.company_name')}</label>
                            <div className="relative group">
                                <Briefcase className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-black transition-colors" size={18} />
                                <input
                                    type="text"
                                    required
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 pl-12 pr-4 outline-none focus:ring-2 focus:ring-black focus:bg-white transition-all text-slate-900"
                                    placeholder={t('placeholders.company_name')}
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1 ml-1">{t('labels.email')}</label>
                        <div className="relative group">
                            <Mail className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-black transition-colors" size={18} />
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 pl-12 pr-4 outline-none focus:ring-2 focus:ring-black focus:bg-white transition-all text-slate-900"
                                placeholder={t('placeholders.email')}
                            />
                        </div>
                    </div>



                    {mode !== 'forgot' && (
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1 ml-1">{t('labels.password')}</label>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-black transition-colors" size={18} />
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 pl-12 pr-4 outline-none focus:ring-2 focus:ring-black focus:bg-white transition-all text-slate-900"
                                    placeholder={t('placeholders.password')}
                                />
                            </div>
                        </div>
                    )}

                    {mode === 'login' && (
                        <div className="flex justify-end">
                            <button type="button" onClick={() => { setMode('forgot'); setError(null); setSuccessMsg(null); }} className="text-sm font-bold text-slate-500 hover:text-black transition">{t('buttons.forgot_password_link')}</button>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-black text-white font-bold py-4 rounded-xl hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg shadow-slate-200"
                    >
                        {loading && <Loader2 className="animate-spin" size={18} />}
                        {mode === 'login' && (loading ? t('buttons.login_loading') : t('buttons.login'))}
                        {mode === 'register' && (loading ? t('buttons.register_loading') : t('buttons.register'))}
                        {mode === 'forgot' && (loading ? t('buttons.forgot_loading') : t('buttons.forgot'))}
                        {!loading && mode !== 'forgot' && <ArrowRight size={18} />}
                    </button>
                </form>

                {/* Footer Links */}
                <div className="mt-8 text-center border-t border-slate-100 pt-6">
                    {mode === 'login' && (
                        <p className="text-slate-500 text-sm">
                            {t('texts.no_account')}{' '}
                            <button onClick={() => { setMode('register'); setError(null); setSuccessMsg(null); }} className="font-bold text-black hover:underline">{t('buttons.switch_signup')}</button>
                        </p>
                    )}
                    {mode === 'register' && (
                        <p className="text-slate-500 text-sm">
                            {t('texts.has_account')}{' '}
                            <button onClick={() => { setMode('login'); setError(null); setSuccessMsg(null); }} className="font-bold text-black hover:underline">{t('buttons.switch_login')}</button>
                        </p>
                    )}
                    {mode === 'forgot' && (
                        <button onClick={() => { setMode('login'); setError(null); setSuccessMsg(null); }} className="font-bold text-slate-500 hover:text-black text-sm flex items-center justify-center gap-2 mx-auto">
                            <ArrowLeft size={16} /> {t('buttons.back_to_login')}
                        </button>
                    )}
                </div>
            </div >

            <div className="mt-8 text-center z-10 flex flex-col items-center gap-2">
                <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-400 hover:text-slate-600 hover:underline transition-colors">
                    Privacy Policy
                </a>
                <div className="text-slate-400 text-xs">
                    &copy; {new Date().getFullYear()} AccountingFlow SaaS. All rights reserved.
                </div>
            </div>
        </div >
    );
};
